/** Width of the left drawer when collapsed to its mini (icon-strip) state. */
export const DRAWER_MINI_WIDTH = 40;

/** Desktop min-width of the pig progress card (PigProgressCard positionStyle). */
export const PIG_PROGRESS_CARD_MIN_WIDTH = 350;

/**
 * Quasar caps a minimized dialog's child at 560px (QDialog.sass
 * `.q-dialog__inner--minimized > div`), so the card is never wider than this.
 * Used as the safe width until the card's rendered width has been measured.
 */
export const PIG_PROGRESS_CARD_MAX_WIDTH = 560;

/** Gap kept between the card and the window edges. */
export const VIEWPORT_EDGE_MARGIN = 8;

/** Current width of the left drawer: the mini width or the full one. */
export function leftDrawerCurrentWidth(
  mini: boolean,
  fullWidth: number
): number {
  return mini ? DRAWER_MINI_WIDTH : fullWidth;
}

export interface MapCenterOffsetInput {
  /** Below the md breakpoint the tracksheet is a dialog and nothing is offset. */
  isMobileLayout: boolean;
  /** True only while the left drawer takes layout space (QDrawer on-layout). */
  leftDrawerOpen: boolean;
  /** Current left drawer width: the mini (collapsed) width or the full one. */
  leftDrawerWidth: number;
  tracksheetOpen: boolean;
  tracksheetWidth: number;
  viewportWidth: number;
  /** Rendered card width; 0 or unknown falls back to the 560px maximum. */
  cardWidth: number;
}

/**
 * The pig progress card is a seamless top-positioned QDialog, so Quasar
 * centres it over the whole viewport. On desktop the left drawer and the
 * tracksheet drawer both take width away from the map, so the visible map
 * centre is shifted by half the difference between the two drawer widths.
 *
 * Returns the horizontal offset (px, positive = right) that moves the card
 * from the viewport centre to the centre of the visible map area.
 *
 * Clamp: when the visible map is narrower than the card, centring would push
 * the card under the left drawer and past the tracksheet at the same time.
 * Then the card's left edge is pinned to the left drawer's edge (so it never
 * covers the left drawer) and it overlaps the tracksheet instead. In every
 * case the card stays inside the viewport, VIEWPORT_EDGE_MARGIN from its edges.
 */
export function mapCenterOffset({
  isMobileLayout,
  leftDrawerOpen,
  leftDrawerWidth,
  tracksheetOpen,
  tracksheetWidth,
  viewportWidth,
  cardWidth,
}: MapCenterOffsetInput): number {
  if (isMobileLayout) return 0;
  const width = Math.min(
    Math.max(
      cardWidth || PIG_PROGRESS_CARD_MAX_WIDTH,
      PIG_PROGRESS_CARD_MIN_WIDTH
    ),
    PIG_PROGRESS_CARD_MAX_WIDTH
  );
  const half = width / 2;
  const left = leftDrawerOpen ? leftDrawerWidth : 0;
  const right = tracksheetOpen ? tracksheetWidth : 0;
  const mapWidth = viewportWidth - left - right;
  let centre = mapWidth < width ? left + half : left + mapWidth / 2;
  centre = Math.min(
    Math.max(centre, half + VIEWPORT_EDGE_MARGIN),
    viewportWidth - half - VIEWPORT_EDGE_MARGIN
  );
  return centre - viewportWidth / 2;
}
